import React, { useEffect, useRef } from 'react';
import { Animated, View } from 'react-native';
import Svg, { Path, G, Defs, LinearGradient, Stop } from 'react-native-svg';

interface ClubIconProps {
  focused: boolean;
  color?: string;
  size?: number;
}

const AnimatedPath = Animated.createAnimatedComponent(Path);
const AnimatedG = Animated.createAnimatedComponent(G);

const ClubIcon: React.FC<ClubIconProps> = ({ focused, color = '#000000', size = 24 }) => {
  const fillOpacity = useRef(new Animated.Value(focused ? 1 : 0)).current;
  const strokeOpacity = useRef(new Animated.Value(focused ? 0 : 1)).current;
  const scale = useRef(new Animated.Value(1)).current;

  useEffect(() => {
    Animated.parallel([
      Animated.spring(fillOpacity, {
        toValue: focused ? 1 : 0,
        useNativeDriver: false,
        tension: 100,
        friction: 7,
      }),
      Animated.spring(strokeOpacity, {
        toValue: focused ? 0 : 1,
        useNativeDriver: false,
        tension: 100,
        friction: 7,
      }),
    ]).start();
  }, [focused]);

  return (
    <Animated.View style={{ transform: [{ scale }] }}>
      <Svg width={size * 1.2} height={size * 1.2} viewBox="0 0 512 512" fill="none">
        <Defs>
          <LinearGradient id="clubGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <Stop offset="0%" stopColor="#D4A655" stopOpacity="1" />
            <Stop offset="50%" stopColor="#C4963E" stopOpacity="1" />
            <Stop offset="100%" stopColor="#A67D2E" stopOpacity="1" />
          </LinearGradient>
        </Defs>
        {/* Filled version (focused) */}
        <AnimatedG opacity={fillOpacity}>
          <AnimatedPath
            d="M223.431,131.351l-64.189-64.189c-6.171-6.171-16.178-6.171-22.348,0L4.629,199.426c-6.171,6.171-6.171,16.177,0,22.348
            l64.189,64.189c6.17,6.17,16.18,6.168,22.348,0l132.266-132.265C229.603,147.527,229.603,137.522,223.431,131.351z"
            fill={focused ? "url(#clubGradient)" : color}
          />
          <AnimatedPath
            d="M507.372,199.425L375.107,67.16c-6.171-6.171-16.178-6.171-22.348,0l-64.189,64.189c-6.171,6.171-6.171,16.177,0,22.348
            l132.266,132.266c6.17,6.17,16.18,6.168,22.348-0.001l64.189-64.189C513.544,215.602,513.544,205.595,507.372,199.425z"
            fill={focused ? "url(#clubGradient)" : color}
          />
          <AnimatedPath
            d="M373.055,280.971c-13.372-13.373-75.959-75.962-98.531-98.527c-6.434-6.431-14.587-10.852-23.407-12.78
            c-4.477,6.404-139.435,140.486-141.67,142.266c1.984,8.636,6.362,16.612,12.68,22.93l95.175,95.163
            c34.206,34.235,68.215,17.49,80.154-6.02l-60.45-60.449c-6.171-6.171-6.171-16.177,0-22.348c6.17-6.171,16.177-6.171,22.348,0
            l60.264,60.264c12.743-6.195,22.683-18.317,25.42-33.444l-56.252-56.252c-6.171-6.171-6.171-16.177,0-22.348
            c6.17-6.171,16.177-6.171,22.348,0l61.975,61.976C392.802,334.269,396.064,303.985,373.055,280.971z"
            fill={color}
          />
        </AnimatedG>
        
        {/* Outline version (not focused) */}
        <AnimatedG opacity={strokeOpacity}>
          <AnimatedPath
            d="M507.371,199.433L375.107,67.167c-6.17-6.171-16.177-6.171-22.348,0l-64.189,64.189c-6.171,6.171-6.171,16.177,0,22.348
            l12.089,12.089l-33.806,2.962c-3.729,0.327-7.358,1.094-10.861,2.263c-3.494-1.166-7.131-1.937-10.844-2.263l-33.807-2.962
            l12.089-12.089c6.171-6.171,6.171-16.177,0-22.348l-64.189-64.189c-6.17-6.171-16.177-6.171-22.348,0L4.629,199.433
            c-6.171,6.17-6.171,16.177,0,22.348l64.189,64.189c6.168,6.168,16.178,6.17,22.348,0l14.298-14.298l2.956,33.799
            c0.968,11.086,5.838,21.526,13.707,29.395l95.175,95.163c26.125,26.125,53.711,23.686,71.568,5.832
            c8.461-8.462,100.662-100.651,101.011-101.001c7.867-7.869,12.733-18.305,13.701-29.389l2.956-33.8l14.298,14.298
            c6.168,6.168,16.178,6.17,22.348,0l64.189-64.189C513.543,215.61,513.543,205.603,507.371,199.433z M79.992,252.448
            l-41.841-41.841L148.069,100.69l41.84,41.84C57.238,275.201,91.124,241.315,79.992,252.448z M353.186,326.807
            c-0.002,0.002-0.004,0.004-0.006,0.006c-5.797,5.804-15.185,5.806-20.996-0.005c-0.01-0.01-0.022-0.019-0.032-0.029
            l-25.233-25.234c-6.172-6.171-16.177-6.17-22.348,0c-6.171,6.171-6.171,16.177,0,22.348l25.265,25.265
            c0.005,0.005,0.011,0.009,0.016,0.014c5.787,5.804,5.801,15.182-0.021,20.992c-5.797,5.803-15.187,5.806-20.999-0.005
            c-0.007-0.007-0.015-0.013-0.021-0.019l-25.244-25.244c-6.17-6.171-16.177-6.171-22.348,0s-6.171,16.177,0,22.348l25.265,25.265
            c0.003,0.003,0.006,0.006,0.01,0.01c5.769,5.782,5.833,15.161-0.026,21.007c-3.471,3.481-8.452,4.999-13.324,4.065
            c-5.309-1.018-7.19-3.604-13.497-9.911l-95.176-95.164c-2.623-2.623-4.246-6.103-4.569-9.799l-5.266-60.216
            c6.604-6.605,36.99-36.998,47.534-47.537l60.217,5.276c3.693,0.323,7.169,1.944,9.789,4.562
            c23.128,23.119,95.053,95.047,101.003,100.999C358.972,311.596,358.974,321.02,353.186,326.807z M373.922,281.845
            c-4.947-4.948-59.152-59.155-83.435-83.433l39.34-3.447c11.237,11.229,44.211,44.214,47.535,47.537L373.922,281.845z
            M432.009,252.448C417.208,237.647,325.361,145.8,322.092,142.531l41.84-41.84L473.85,210.606L432.009,252.448z"
            fill={color}
            strokeWidth="6"
          />
        </AnimatedG>
      </Svg>
    </Animated.View>
  );
};

export default ClubIcon;
